# Motorway Collect App - Privacy Policy
<br/>

Motorway Online Ltd (“**Motorway**”, “**we**”, “**us**” or “**our**”) is an online used car marketplace. Save for where specified otherwise, capitalised terms on this page shall bear the same meanings as those given to them in our Motorway Collect App Terms and Conditions.
<br/>

For the purpose of UK data protection law, the controller is Motorway Online Ltd (Company Number 10285711) of International House, 24 Holborn Viaduct, London EC1A 2BN.
<br/>

**What is this document?**
<br/>

This Privacy Policy applies to your use of the Motorway Collect App (“**App**”).
<br/>

The App will enable you to carry out vehicle inspections when carrying out Motorway Collections. The App features image and video capture technology, along with the ability for you to input text and note undisclosed damage on vehicles.
<br/>

To enable this, we need to collect certain information about you and your use of the App. This Privacy Policy details the types of personal information we collect and process, what we use that information for, how long we use it, who we share it with and why. Please read this policy carefully and contact us using the details set out below, if you have any questions.
<br/>

You can contact us at any point about anything in this Privacy Policy by emailing [privacy\@motorway.co.uk](mailto:privacy@motorway.co.uk).
<br/>

**Information that we collect** \
When you use the App, we will collect certain information about you, such as:
- basic information which you provide to us;
- your account details and account profile;
- records of how you use the App;
- records of communications you have with us and dealers through the App;
- information we obtain about your location when carrying out Motorway Collections; and
- technical information about your device and App usage, including cookies.
<br/>

<ins>Basic information which you provide to us</ins> \
If you sign up to use the App (even if you don’t carry out a Motorway Movement using the App), we may collect your name, address, email address, telephone number, and other basic information. We will use this information to create an account for you to manage your Motorway Collections.
<br/>

<ins>Your account details and account profile</ins> \
When you sign up to use the App, we will create an account for you. You will need to create a profile and password, which you should keep safe and not share with anyone.
<br/>

<ins>Records of how you use the App</ins> \
We will keep a record of the Motorway Collections you complete using the records of how you use the App. This may include details of your use of the App (captured through cookies and similar tracking technologies). We may also track how you interact with the App compared to other drivers to assess where we can make improvements to assist you with your Motorway Collections.
<br/>

<ins>Records of communications you have with us</ins> \
If you contact us with a question or query, or to provide us with feedback, we will keep a record of your communications with us. We will also keep copies of any correspondence you have with us or a dealer through the App.
<br/>

<ins>Information we obtain on your location when carrying out Motorway Collections</ins> \
We may enable GPS tracking as a background technology when you use the App. This is to enable us to keep sellers and dealers updated as to the location of their vehicles and for your safety. This will not be enabled when you are not carrying out Motorway Collections.
<br/>

<ins>Technical information about your device and App usage</ins> \
When you use the App, we collect certain technical information including:
- details about your device, such as your IP address, device type, and operating system;
- information relating to use of the App, such as the pages you visit and elements of each page you interact with. We do this through the deployment of cookies and similar technologies on your device.
<br/>

**How we use your personal information** \
We use personal information about you for various purposes connected with Motorway Collections, including:
- to facilitate the collection and delivery of vehicles from the seller to the car buying partner;
- to communicate with you in relation to Motorway Collections;
- to communicate with third parties in relation to Motorway Collections; and
- for internal management, administrative and organisational reasons.
<br/>

To use your personal information, we must have a legal basis for doing so. The legal bases that we rely upon are explained further in the table below. We will only use your personal information where it is necessary.
<br />

<table data-mdx="table" border="solid">
	<tbody>
	<tr>
		<th>Purpose</th>
		<th>Method of collection and legal basis for processing</th>
	</tr>
	<tr>
		<td>
			**In order to enable you to use the App, we need to collect and share your information in the following ways:**
			- to connect you with car buying partners in relation to Motorway Collections; and
			- to the Seller of a Vehicle of the details relating to the Motorway Movement.
		</td>
		<td>
			**Method of collection** \
			This information will be provided by you directly when you first sign up for the App and set up an account with us and then during the course of using the App.
			<br/>
			**Legal basis** \
			This is necessary for the performance of the contract between (a) you and our transport partner, and (b) you and us, in relation your use of the App.
		</td>
	</tr>
	<tr>
		<td>
			**To communicate with you:**
			- we will provide you with support and answers and/or respond to any queries or feedback that you send to us;
			- to assist you with any technical problems you have with the App;
			- to collect reviews of our services in order to improve our business;
			- to update you with any changes to our terms and conditions/other policies.
		</td>
		<td>
			**Method of collection** \
			This information is collected any time when we communicate with you, or you communicate with us.
			<br/>
			**Legal basis** \
			It is our legitimate business interest to inform you of relevant information in relation to the services that we provide, respond to your communications and to improve our business.
		</td>
	</tr>
	<tr>
		<td>
			**Internal management, administrative and organisational reasons:**
			- to manage the App;
			- to administer and manage our services and business in an efficient and proper way (e.g., accounting, billing, etc.);
			- to manage how we work with other companies that provide services to us (e.g., transport providers);
			- to establish and protect our business and legal rights or to defend ourselves against any legal actions; and
			- to comply with legal requirements and obligations to which we are subject.
		</td>
		<td>
			**Method of collection** \
			This information is collected from you both directly and indirectly.
			<br/>
			**Legal basis** \
			It is our legitimate business interest to manage and administer the App and comply with laws to which we are subject and protect our business interests and legal rights.
		</td>
	</tr>
	<tr>
		<td>
			**To share information with service car buying partners:**
			- to enable them to assess the condition of a vehicle;
			- for the purposes of negotiation on price, should a vehicle not be in the same condition as described by a seller.
		</td>
		<td>
			**Method of collection** \
			This may include any of the information we hold about you and location data, whether collected by direct or indirect means.
			<br/>
			**Legal basis** \
			It is our legitimate business interest to share your information with trusted third parties who have a vested interest in the Motorway Collections.
		</td>
	</tr>
	</tbody>
</table>
<br/>

**Who do we share your information with, and for what purposes?** \
We may share your personal information with:
- our transport partners, for bill reconciliation, administrative, and other purposes;
- a seller of a vehicle, in relation to a Motorway Movement that you are working on;
- a buyer of a vehicle, in relation to a Motorway Movement that you are working on;
- law enforcement authorities, in relation to a legal matter surrounding a Motorway Movement; and
- emergency services, where we see that you have been involved in an accident.
<br/>

**Storage of your personal information** \
Information about you may be transferred outside the UK to a country, territory or international organisation that may not have UK equivalent data protection standards.
<br />
This may be when we transfer your personal information to our trusted third-party service providers (for the purposes described above) outside of the European Union. In such cases, we will ensure that your personal information is protected by implementing appropriate safeguards. If you would like more information about any of the transfer safeguards on which we rely please contact us as set out in this Privacy Policy.
<br />

**How do we protect your personal information?** \
We implement appropriate technical and organisational measures to protect personal information that we hold from unauthorised disclosure, use, alteration or destruction. Where appropriate, we use encryption and other technologies that can assist in securing the information you provide. We also require our service providers to comply with strict data privacy requirements.
<br />

**How long will your personal information be kept?** \
The period for which we may retain information about you will depend on the purposes for which the information was collected, whether you have requested the deletion of the information, and whether any legal obligations require the retention of certain information (for example, for regulatory compliance). We will not retain information about you for longer than is necessary to fulfil the purposes for which it was collected.
<br />

**Your rights** \
You may have some or all the following rights in respect of the information about you that we process:
- request us to give you access to it;
- request us to rectify it, update it, or erase it;
- request us to restrict our using it, in certain circumstances;
- object to our using it, in certain circumstances;
- withdraw your consent to our using it (where we have initially relied on consent)
- data portability, in certain circumstances;
- the right to opt out from our using it for direct marketing; and
- the right to lodge a complaint with the Information Commissioner’s Office, which is responsible for upholding your data protection rights in the UK.
<br />

**You are able to exercise these rights by contacting us, using the details set out at the beginning of this Privacy Policy.**